import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

// Performance monitoring utilities
export const initPerformanceTracking = (trackPerformance: (metric: string, value: number) => void) => {
  // Track Core Web Vitals
  getCLS((metric) => {
    trackPerformance('CLS', metric.value);
  });

  getFID((metric) => {
    trackPerformance('FID', metric.value);
  });

  getFCP((metric) => {
    trackPerformance('FCP', metric.value);
  });

  getLCP((metric) => {
    trackPerformance('LCP', metric.value);
  });

  getTTFB((metric) => {
    trackPerformance('TTFB', metric.value);
  });

  // Track additional performance metrics
  if ('performance' in window) {
    // Track page load time
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        const loadTime = navigation.loadEventEnd - navigation.fetchStart;
        trackPerformance('Page Load Time', loadTime);
      }
    });

    // Track resource loading performance
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'resource') {
          const resource = entry as PerformanceResourceTiming;
          if (resource.duration > 1000) { // Only track slow resources (>1s)
            trackPerformance('Slow Resource', resource.duration);
          }
        }
      });
    });

    observer.observe({ entryTypes: ['resource'] });
  }
};

// Track user interaction timing
export const trackUserInteraction = (action: string, startTime: number) => {
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  // Track interactions that take longer than 100ms
  if (duration > 100) {
    console.log(`Slow interaction: ${action} took ${duration}ms`);
  }
};

// Monitor memory usage (if available)
export const trackMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit
    };
  }
  return null;
};

// Track page visibility changes
export const initVisibilityTracking = (trackEvent: (event: any) => void) => {
  let visibilityStartTime = Date.now();
  let isVisible = true;

  const handleVisibilityChange = () => {
    const now = Date.now();
    
    if (document.hidden) {
      // Page became hidden
      if (isVisible) {
        const timeVisible = now - visibilityStartTime;
        trackEvent({
          category: 'Engagement',
          action: 'Page Visibility',
          label: 'Time Visible',
          value: Math.round(timeVisible / 1000) // Convert to seconds
        });
        isVisible = false;
      }
    } else {
      // Page became visible
      visibilityStartTime = now;
      isVisible = true;
      trackEvent({
        category: 'Engagement',
        action: 'Page Visibility',
        label: 'Page Visible',
        value: 1
      });
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

// Track connection quality
export const trackConnectionQuality = (trackEvent: (event: any) => void) => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    
    trackEvent({
      category: 'Performance',
      action: 'Connection Quality',
      label: connection.effectiveType || 'unknown',
      value: connection.downlink || 0
    });
  }
};

// Track device capabilities
export const trackDeviceCapabilities = (trackEvent: (event: any) => void) => {
  const capabilities = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1
  };

  trackEvent({
    category: 'Device',
    action: 'Capabilities',
    label: `${capabilities.platform} - ${capabilities.screenWidth}x${capabilities.screenHeight}`,
    value: capabilities.pixelRatio
  });
};
